import { useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Printer } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-hot-toast';
import { OrderPdfPreview } from '../[id]/OrderPdfPreview';
import { DeleoPackingVerificationPage } from '../[id]/DeleoPackingVerificationPage';
import type { OrderSummary } from '../../../../infrastructure/OMSClient';
import { ShippingProviderType } from '../../../../infrastructure/OMSClient';
import { useShipping } from '../../../../apps/hooks/shipping/useShipping';
import { appendPackingVerificationPages as appendParxlPackingVerificationPages } from '../utils/parxlPackingVerificationPdf';

interface OrderLabelPrintButtonProps {
  order: OrderSummary;
  variant?: string;
  size?: 'sm' | 'lg';
  showText?: boolean;
}

const getCurrentShippingProvider = (order: OrderSummary): ShippingProviderType | null => {
  if (order.shippingData?.provider) {
    return order.shippingData.provider;
  }
  if (order.parxlData) {
    return ShippingProviderType.PARXL;
  }
  const hasYUN = order.deleoData?.orderTrackingList?.some((tracking) =>
    tracking.deliveryCompanyCd?.name?.toUpperCase().includes('YUN'),
  );
  if (hasYUN) {
    return ShippingProviderType.YUN;
  }
  if (order.deleoData?.deleoOrderId) {
    return ShippingProviderType.DELEO;
  }
  return null;
};

export function OrderLabelPrintButton({
  order,
  variant = 'outline-primary',
  size = 'sm',
  showText = true,
}: OrderLabelPrintButtonProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const { downloadShippingLabel, isDownloadingLabels } = useShipping();

  const handleDeleoPrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
        size: 15cm 10cm !important;
        margin: 0 !important;
      }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        html, body {
          margin: 0 !important;
          padding: 0 !important;
        }
        .modal-body,
        .modal-content,
        .modal {
          margin: 0 !important;
          padding: 0 !important;
          max-height: none !important;
          overflow: visible !important;
          border: none !important;
          box-shadow: none !important;
        }
        .print-area {
          width: 15cm !important;
          height: 10cm !important;
          box-sizing: border-box !important;
          margin: 0 !important;
          page-break-after: always !important;
        }
        .deleo-verification-page {
          width: 15cm !important;
          height: 10cm !important;
          margin: 0 !important;
          page-break-before: always !important;
        }
      }
    `,
  });

  const handlePrintWithProvider = async (provider: ShippingProviderType, providerOrderId: string) => {
    setIsPrinting(true);
    try {
      const response = await downloadShippingLabel({ provider, orderId: order.orderId, providerOrderId });
      console.info('Download label response:', response);

      if (!response.success) {
        toast.error(`Failed to get label: ${response.message || 'Unknown error'}`);
        return;
      }

      if (!response.labels || response.labels.length === 0) {
        toast.error('No labels returned from server');
        return;
      }

      if (!response.labels[0]?.pdfData) {
        toast.error('Label PDF data is missing');
        return;
      }

      const byteCharacters = atob(response.labels[0].pdfData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const originalPdfBytes = new Uint8Array(byteNumbers);
      const modifiedPdfBytes = await appendParxlPackingVerificationPages(originalPdfBytes, order);
      const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Print label error:', error);
      toast.error(`Failed to print label: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePrint = async () => {
    const provider = getCurrentShippingProvider(order);

    switch (provider) {
      case ShippingProviderType.PARXL:
        {
          const providerOrderId = order.parxlData?.plsShipmentReference || order.shippingData?.providerOrderId;
          if (!providerOrderId) {
            toast.error('No Parxl tracking ID found');
            return;
          }
          await handlePrintWithProvider(ShippingProviderType.PARXL, providerOrderId);
        }
        break;
      case ShippingProviderType.YUN:
        {
          const providerOrderId = order.shippingData?.providerOrderId;
          if (!providerOrderId) {
            toast.error('No YUN tracking ID found');
            return;
          }
          await handlePrintWithProvider(ShippingProviderType.YUN, providerOrderId);
        }
        break;
      case ShippingProviderType.DELEO:
      default:
        handleDeleoPrint();
        break;
    }
  };

  const provider = getCurrentShippingProvider(order);
  const isLoading = isPrinting || isDownloadingLabels;
  const buttonLabel =
    provider === ShippingProviderType.PARXL
      ? 'Print (Parxl)'
      : provider === ShippingProviderType.YUN
        ? 'Print (YUN)'
        : provider === ShippingProviderType.DELEO
          ? 'Print (DELEO)'
          : 'Print';

  return (
    <>
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }}>
        <div ref={printRef}>
          <OrderPdfPreview order={order} />
          <DeleoPackingVerificationPage order={order} />
        </div>
      </div>
      <Button
        variant={variant}
        size={size}
        onClick={handlePrint}
        disabled={isLoading}
        style={{ fontSize: '12px', padding: '4px 12px' }}
      >
        {isLoading ? <Spinner animation="border" size="sm" className="me-1" /> : <Printer className="h-4 w-4 me-1" />}
        {showText && buttonLabel}
      </Button>
    </>
  );
}
